import React from 'react';
import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Layout,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { DataCollectionCTAs } from '../components/dataCollectionCTAs';
import { CtaType } from '@esportal/portal-service';
import { ClimaproSpecialCTA } from '../components/climproSpecialCTA';
import { PortalContext } from '../../../context/PortalContext';
import { getUserName } from '../../../helpers/nameHelper';
import {
	DataCollectionStep,
	EndFormBackgroundContainer,
	formLayout,
	RelativeContainer,
	StyledIcon,
} from '../components/dataCollectionForm';
import { MasterAsfaltoIcon } from './forms-icons/master-asalfto-icon';
import { useTrackingManager } from '../../../tracking/useTrackingManager';
import { formatOwnership } from '../../../tracking/trackingHelper';
import { FormDataCollection } from './types';

interface StepsState {
	previousStep: number;
	actualStep: number;
}

interface Form2Props {
	handleLinealNextStep: (answer?: string[]) => void;
	updateVisibleSteps: (newSteps: string[], isSelected: boolean) => void;
	getInitialSteps: (steps: DataCollectionStep[]) => void;
	steps: StepsState;
}

export const Form2: React.FC<Form2Props> = ({ handleLinealNextStep, updateVisibleSteps, getInitialSteps, steps }) => {
	const { portalService } = React.useContext(PortalContext);

	const formName = 'vw-club-data_collection-2';

	const userData = portalService.getPerson().getValue();
	const userName = getUserName(userData);

	const [answer, setAnswer] = React.useState<string[]>([]);
	const [totalSteps, setTotalSteps] = React.useState(0);
	const userVehicles = portalService.getPersonVehicles().getValue();
	const [isFormLoadTracked, setIsFormLoadTracked] = React.useState<boolean>(false);

	const trackingManager = useTrackingManager();

	React.useEffect(() => {
		if (userVehicles && steps.actualStep !== 0 && !isFormLoadTracked) {
			trackingManager.trackClubVWFormLoad(
				{
					FormName: formName,
					FormStart: true,
					FormTypeMultiple: ['otherform'],
				},
				{ contentId: formName },
				{ vehicle: formatOwnership(userVehicles?.vehicles) },
			);
			setIsFormLoadTracked(true);
		}
	}, [userVehicles, steps, isFormLoadTracked]);

	const handleLinealNextStepOnClick = (answer: string[], stepName: string) => {
		trackingManager.trackClubVWFormButtonClick(
			{
				FormName: formName,
				FormStart: false,
				FormTypeMultiple: ['otherform'],
			},
			{ contentId: `Listo - Answered step: ${stepName}` },
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
		handleLinealNextStep(answer);
		setAnswer([]);
	};

	const handleAnswerChange = (value: string[]) => {
		setAnswer(value);
	};

	const handleBackOnClick = () => {
		handleLinealNextStep(['COMPLETED_ALL_FORMS']);
		trackingManager.trackClubVWButtonClick(
			'Volver',
			{ contentId: 'Back to Dashboard' },
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
		trackingManager.trackClubVWLayerCloseClick('Data Collection Layer', { contentId: 'Data Collection' });
	};

	const form2: FormDataCollection[] = [
		{
			name: 'INTRO_FORM',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.headline400}>
							¡Queremos <Text bold>adaptarnos a ti!</Text>
						</Text>
					</Container>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy100}>
							Cada conductor, cada pasajero y cada viaje es único. Y tu experiencia con nosotros, también.
						</Text>
						<Text appearance={TokenTextAppearance.bigcopy100}>
							Cuanto más sepamos de ti, más personalizada será. Así que, ¿nos conocemos?
						</Text>
					</Container>
					<Container>
						<CTA
							tag="button"
							emphasis="primary"
							onClick={() => {
								handleLinealNextStep();
								trackingManager.trackClubVWButtonClick(
									'Empezar',
									{ contentId: `Start ${formName}` },
									{ vehicle: formatOwnership(userVehicles?.vehicles) },
								);
							}}
						>
							<Text>Empezar</Text>
						</CTA>
					</Container>
				</Container>
			),
			userLevel: 2,
			visibleStep: true,
		},
		{
			name: 'INTERESES_COMO_ERES',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Si tuvieras que presentarte a un desconocido...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Qué 3 adjetivos </Text>crees que te definen mejor?
						</Text>
					</Container>
					<DataCollectionCTAs
						type={CtaType.CHECKBOX}
						ctaAnswers={[
							{ label: '🏃‍♂️ Deportista', value: 'deporte' },
							{ label: '📚 Cultureta', value: 'cultura' },
							{ label: '🎵 Music lover', value: 'musica' },
							{ label: '🤝 Sociable', value: 'social' },
							{ label: '🎨 Artista', value: 'arte' },
							{ label: '🍳 Cocinitas', value: 'gastronomia' },
							{ label: '🧳 Viajero', value: 'viajes' },
							{ label: '🐶 Animal lover', value: 'animales' },
							{ label: '🎉 Fiestero', value: 'fiesta' },
						]}
						updateVisibleSteps={updateVisibleSteps}
						handleLinealNextStep={handleLinealNextStep}
						handleAnswerChange={handleAnswerChange}
						stepName="INTERESES_COMO_ERES"
						userLevel={2}
					/>
					<Container
						padding={{
							top: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<CTA
							tag="button"
							emphasis="primary"
							disabled={answer.length == 0}
							onClick={() => {
								handleLinealNextStepOnClick(answer, 'INTERESES_COMO_ERES');
							}}
						>
							<Text bold>Listo</Text>
						</CTA>
					</Container>
				</Container>
			),
			userLevel: 2,
			visibleStep: true,
		},
		{
			name: 'INTERESES_PREFERENCIA',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Oye, juguemos a “esto o lo otro”...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Qué prefieres </Text>cuando vas en coche?
						</Text>
					</Container>
					<Container
						padding={{
							right: {
								[Breakpoints.b1280]: ContainerPadding.grid006,
								[Breakpoints.b1920]: ContainerPadding.grid005,
								[Breakpoints.b2560]: ContainerPadding.grid004,
							},
						}}
						stretchContent
					>
						<ClimaproSpecialCTA
							ctaAnswers={[
								{ label: '🚗 Conducir', value: 'conducir' },
								{ label: '🕶️ Que te lleven', value: 'lleven' },
							]}
							actualStep={steps.actualStep}
							totalSteps={totalSteps}
							handleLinealNextStep={handleLinealNextStep}
							stepName="INTERESES_PREFERENCIA"
							userLevel={2}
						/>
					</Container>
				</Container>
			),
			userLevel: 2,
			visibleStep: true,
		},
		{
			name: 'INTERESES_PREFERENCIA',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Oye, juguemos a “esto o lo otro”...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Qué prefieres </Text>cuando vas en coche?
						</Text>
					</Container>
					<Container
						padding={{
							right: {
								[Breakpoints.b1280]: ContainerPadding.grid006,
								[Breakpoints.b1920]: ContainerPadding.grid005,
								[Breakpoints.b2560]: ContainerPadding.grid004,
							},
						}}
						stretchContent
					>
						<ClimaproSpecialCTA
							ctaAnswers={[
								{ label: '🏠 Viajes cortos', value: 'cortos' },
								{ label: '🌄 Viajes largos', value: 'largos' },
							]}
							actualStep={steps.actualStep}
							totalSteps={totalSteps}
							handleLinealNextStep={handleLinealNextStep}
							stepName="INTERESES_PREFERENCIA"
							userLevel={2}
						/>
					</Container>
				</Container>
			),
			userLevel: 2,
			visibleStep: true,
		},
		{
			name: 'INTERESES_PREFERENCIA',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Oye, juguemos a “esto o lo otro”...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Qué prefieres </Text>cuando vas en coche?
						</Text>
					</Container>
					<Container
						padding={{
							right: {
								[Breakpoints.b1280]: ContainerPadding.grid006,
								[Breakpoints.b1920]: ContainerPadding.grid005,
								[Breakpoints.b2560]: ContainerPadding.grid004,
							},
						}}
						stretchContent
					>
						<ClimaproSpecialCTA
							ctaAnswers={[
								{ label: '☎️ Hablar por teléfono', value: 'telefono' },
								{ label: '📵 Desconectar el móvil', value: 'desconectar' },
							]}
							actualStep={steps.actualStep}
							totalSteps={totalSteps}
							handleLinealNextStep={handleLinealNextStep}
							stepName="INTERESES_PREFERENCIA"
							userLevel={2}
						/>
					</Container>
				</Container>
			),
			userLevel: 2,
			visibleStep: true,
		},
		{
			name: 'INTERESES_PREFERENCIA',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Oye, juguemos a “esto o lo otro”...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Qué prefieres </Text>cuando vas en coche?
						</Text>
					</Container>
					<Container
						padding={{
							right: {
								[Breakpoints.b1280]: ContainerPadding.grid006,
								[Breakpoints.b1920]: ContainerPadding.grid005,
								[Breakpoints.b2560]: ContainerPadding.grid004,
							},
						}}
						stretchContent
					>
						<ClimaproSpecialCTA
							ctaAnswers={[
								{ label: '🎵 Escuchar música', value: 'musica' },
								{ label: '🎙️ Escuchar un podcast', value: 'podcast' },
							]}
							actualStep={steps.actualStep}
							totalSteps={totalSteps}
							handleLinealNextStep={handleLinealNextStep}
							stepName="INTERESES_PREFERENCIA"
							userLevel={2}
						/>
					</Container>
				</Container>
			),
			userLevel: 2,
			visibleStep: true,
		},
		{
			name: 'INTERESES_PREFERENCIA',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Oye, juguemos a “esto o lo otro”...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Qué prefieres </Text>cuando vas en coche?
						</Text>
					</Container>
					<Container
						padding={{
							right: {
								[Breakpoints.b1280]: ContainerPadding.grid006,
								[Breakpoints.b1920]: ContainerPadding.grid005,
								[Breakpoints.b2560]: ContainerPadding.grid004,
							},
						}}
						stretchContent
					>
						<ClimaproSpecialCTA
							ctaAnswers={[
								{ label: '💬 Hablar con tus acompañantes', value: 'hablar' },
								{ label: '🤫 Conducir en silencio', value: 'silencio' },
							]}
							actualStep={steps.actualStep}
							totalSteps={totalSteps}
							handleLinealNextStep={handleLinealNextStep}
							stepName="INTERESES_PREFERENCIA"
							userLevel={2}
						/>
					</Container>
				</Container>
			),
			userLevel: 2,
			visibleStep: true,
		},
		{
			name: 'END_FORM',
			screen: (
				<EndFormBackgroundContainer className="end-form-2__background">
					<Layout data-testid="end-form__layout" appearance={formLayout} allowOverflowingContent>
						<RelativeContainer className="end-form__relative-container">
							<Container
								verticalAlign={'center'}
								horizontalAlign={'center'}
								wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b960]: 'never' }}
								gutter={{ [Breakpoints.b960]: ContainerGutter.grid001 }}
								shrinkContent
							>
								<Container
									horizontalAlign={'center'}
									verticalAlign={'center'}
									padding={{
										bottom: {
											[Breakpoints.default]: ContainerPadding.static500,
											[Breakpoints.b960]: ContainerPadding.static0,
										},
									}}
								>
									<StyledIcon className="icon-container">
										<MasterAsfaltoIcon />
									</StyledIcon>
								</Container>
								<Container
									horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
									verticalAlign={'center'}
									wrap={'always'}
								>
									<Container
										horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
										verticalAlign={'center'}
										padding={{
											bottom: ContainerPadding.static200,
										}}
										wrap={'always'}
										shrinkContent
									>
										<Text
											appearance={TokenTextAppearance.headline400}
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b960]: TextAlignment.left,
											}}
											bold
										>
											¡Ya eres Máster del asfalto!
										</Text>
									</Container>
									<Container
										horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
										verticalAlign={'center'}
										wrap={'always'}
										padding={{
											bottom: {
												[Breakpoints.default]: ContainerPadding.static200,
											},
										}}
									>
										<Text
											appearance={TokenTextAppearance.bigcopy100}
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b960]: TextAlignment.left,
											}}
										>
											Felicidades, has llegado a la meta. Muchas gracias por compartir todo esto con nosotros,{' '}
											{userName}. Nos ha encantado concerte un poco más.
										</Text>
									</Container>
									<Container
										horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
										verticalAlign={'center'}
										wrap={'always'}
										padding={{
											bottom: {
												[Breakpoints.default]: ContainerPadding.static500,
												[Breakpoints.b960]: ContainerPadding.static520,
											},
										}}
									>
										<Text
											appearance={TokenTextAppearance.headline200}
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b960]: TextAlignment.left,
											}}
											bold
										>
											Próximamente, tendremos nuevos retos para ti.
										</Text>
									</Container>
									<Text
										textAlign={{
											[Breakpoints.default]: TextAlignment.center,
											[Breakpoints.b960]: TextAlignment.left,
										}}
									>
										<CTA tag="button" emphasis="primary" onClick={handleBackOnClick}>
											Volver
										</CTA>
									</Text>
								</Container>
							</Container>
						</RelativeContainer>
					</Layout>
				</EndFormBackgroundContainer>
			),
			userLevel: 2,
			visibleStep: true,
		},
	];

	React.useEffect(() => {
		const findDataCollectionCTAs = (children: any) => {
			let ctaAnswers: any = [];

			React.Children.forEach(children, (child) => {
				if (!child) return;

				if (child.type === DataCollectionCTAs) {
					ctaAnswers.push(...(child.props.ctaAnswers || []));
				}

				if (child.props && child.props.children) {
					ctaAnswers = ctaAnswers.concat(findDataCollectionCTAs(child.props.children));
				}
			});

			return ctaAnswers;
		};

		const stepNamesAndVisibility = form2.map((step) => {
			const ctaAnswers = findDataCollectionCTAs(step.screen.props.children);

			return {
				name: step.name,
				visibleStep: step.visibleStep,
				questionLevel: step.userLevel,
				ctaAnswers: ctaAnswers.length > 0 ? ctaAnswers : null,
			};
		});

		setTotalSteps(form2.length);
		getInitialSteps(stepNamesAndVisibility);
		setAnswer([]);
	}, []);

	const currentFormScreen = form2[steps.actualStep].screen;

	return <>{currentFormScreen}</>;
};
